import type { User } from "@/auth/authSlice";
import type { PlatformSessionState } from "@/features/platformOwner/platformSessionSlice";

/**
 * Single owner of the persisted auth envelope.
 *
 * `auth_session_v1` is the only thing that survives a hard refresh, so it — not Redux — is
 * the client-side source of truth for "who am I on reload". Everything the app must know
 * after F5 belongs in this one envelope; adding parallel localStorage flags for individual
 * facts is what lets them drift apart (the support-session banner disappearing on refresh
 * while the swapped identity survived was exactly that drift, in reverse).
 *
 * The tokens themselves stay in their own long-standing `accessToken`/`tokenExpiry`/
 * `refreshToken` keys, which axios reads directly on every request.
 */
const AUTH_SESSION_STORAGE_KEY = "auth_session_v1";

export interface StoredAuthSession {
  user: User;
  effectivePermissionKeys: string[];
  permissionVersion?: number;
  tokenExpiry: string;
  /**
   * Present only while a Platform Support Session is active. Holds the stashed platform-owner
   * credentials Exit needs, so ending the session still works after a reload — before this was
   * persisted, a refresh stranded the operator inside the tenant with no way back to /platform.
   */
  platformSession?: PlatformSessionState;
}

export function readStoredAuthSession(): StoredAuthSession | null {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAuthSession;
  } catch {
    return null;
  }
}

/**
 * Merges `patch` into the stored envelope, leaving untouched fields alone. A key set to
 * `undefined` is dropped from the envelope entirely (JSON.stringify omits it), which is how
 * exiting a support session clears `platformSession`.
 *
 * No-ops when nothing is stored yet: this only ever keeps an existing session in sync, it
 * must never resurrect one that was logged out.
 */
export function patchStoredAuthSession(patch: Partial<StoredAuthSession>): void {
  try {
    const session = readStoredAuthSession();
    if (!session) return;
    localStorage.setItem(
      AUTH_SESSION_STORAGE_KEY,
      JSON.stringify({ ...session, ...patch }),
    );
  } catch {
    // Storage can be unavailable (private mode, quota); a failed sync must never break the
    // in-memory session that already succeeded.
  }
}
